/**
 * Messages State
 * Per-room message atoms using Jotai for efficient updates
 */
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
// ============================================================================
// Message Atoms (per room)
// ============================================================================
/**
 * Messages atom family - one atom per room JID
 */
export const messagesAtomFamily = atomFamily((_roomJid) => atom([]));
/**
 * Loading state atom family - one per room JID
 */
export const messagesLoadingAtomFamily = atomFamily((_roomJid) => atom(false));
// ============================================================================
// Global Message Handler Atom
// ============================================================================
/**
 * Backend reference for message operations
 */
export const messageBackendAtom = atom(null);
// ============================================================================
// Derived Atoms
// ============================================================================
/**
 * Get messages for a specific room
 */
export const getRoomMessagesAtom = (roomJid) => atom((get) => get(messagesAtomFamily(roomJid)));
/**
 * Get loading state for a specific room
 */
export const getRoomLoadingAtom = (roomJid) => atom((get) => get(messagesLoadingAtomFamily(roomJid)));
// ============================================================================
// Actions (Write-only atoms)
// ============================================================================
/**
 * Add a message to a room
 */
export const addMessageAtom = atom(null, (get, set, { roomJid, message }) => {
    const messagesAtom = messagesAtomFamily(roomJid);
    const currentMessages = get(messagesAtom);
    // Avoid duplicates
    if (currentMessages.some((m) => m.id === message.id)) {
        return;
    }
    set(messagesAtom, [...currentMessages, message]);
});
/**
 * Set all messages for a room (e.g., after MAM query)
 */
export const setRoomMessagesAtom = atom(null, (_get, set, { roomJid, messages }) => {
    const messagesAtom = messagesAtomFamily(roomJid);
    set(messagesAtom, messages);
});
/**
 * Clear messages for a room
 */
export const clearRoomMessagesAtom = atom(null, (_get, set, roomJid) => {
    const messagesAtom = messagesAtomFamily(roomJid);
    set(messagesAtom, []);
});
/**
 * Load archived messages for a room
 */
export const loadArchivedMessagesAtom = atom(null, async (get, set, { roomJid, limit }) => {
    const backendRef = get(messageBackendAtom);
    if (!backendRef?.backend) {
        throw new Error('Backend not initialized');
    }
    const loadingAtom = messagesLoadingAtomFamily(roomJid);
    set(loadingAtom, true);
    try {
        const result = await backendRef.backend.queryArchive(roomJid, limit !== undefined ? { paging: { max: limit } } : {});
        const messagesAtom = messagesAtomFamily(roomJid);
        // Extract messages from MAMResult items
        const messages = (result.results || [])
            .map((r) => r.item.message)
            .filter((m) => m !== undefined);
        set(messagesAtom, messages);
    }
    catch (error) {
        console.error('[Messages] Load archived messages failed:', error);
        throw error;
    }
    finally {
        set(loadingAtom, false);
    }
});
// ============================================================================
// Utility Functions
// ============================================================================
/**
 * Sort messages by timestamp
 */
export function sortMessages(messages) {
    return [...messages].sort((a, b) => {
        // Handle both Date objects and ISO timestamp strings
        const getTime = (msg) => {
            const timestamp = msg.delay?.timestamp;
            if (!timestamp) {
                return Date.now();
            }
            if (timestamp instanceof Date) {
                return timestamp.getTime();
            }
            // Handle string timestamps (ISO format from storage)
            if (typeof timestamp === 'string') {
                return new Date(timestamp).getTime();
            }
            // Fallback
            return Date.now();
        };
        return getTime(a) - getTime(b);
    });
}
/**
 * Get message sender nickname (from room JID format: room@conference/nickname)
 */
export function getMessageSender(message) {
    if (!message.from)
        return 'Unknown';
    if (message.type === 'groupchat') {
        const parts = message.from.split('/');
        return parts[1] || 'Unknown';
    }
    return message.from;
}
/**
 * Check if message is from current user
 */
export function isOwnMessage(message, currentNickname) {
    if (!currentNickname)
        return false;
    const sender = getMessageSender(message);
    return sender === currentNickname;
}
//# sourceMappingURL=messages.js.map